/**
 * SmartCaretManager - Intelligent caret positioning with context awareness
 * 
 * Provides sophisticated caret management for legal document editing:
 * - Context-aware positioning (citations, clauses, paragraphs)
 * - Smooth animations and visual feedback
 * - Legal document structure understanding
 * - Performance-optimized hit testing integration
 * - Accessibility and keyboard navigation support
 */

import { PrecisionHitTestingEngine } from '../engines/PrecisionHitTestingEngine';
import { CaretPerformanceMonitor } from '../monitoring/CaretPerformanceMonitor';
import { UnifiedCoordinateSystem, CoordinateSpace } from '../core/UnifiedCoordinateSystem';
import { 
  CaretPosition, 
  CaretTarget, 
  KeyboardNavigation, 
  EditOperation,
  CaretSystemConfig,
  SystemState,
  ValidationResult
} from '../interfaces/CaretInterfaces';

export interface CaretState {
  position: CaretPosition | null;
  isVisible: boolean;
  isBlinking: boolean;
  lastUpdateTime: number;
  animationState: 'idle' | 'moving' | 'appearing' | 'disappearing';
  context: CaretContext;
}

export interface CaretContext {
  // Document structure context
  isInParagraph: boolean;
  isInCitation: boolean;
  isInClause: boolean;
  isInHeader: boolean;
  isInFooter: boolean;
  
  // Legal document context
  citationId?: string;
  clauseNumber?: string;
  paragraphType?: 'normal' | 'heading' | 'list' | 'quote';
  
  // Editing context
  isAtWordBoundary: boolean;
  isAtLineStart: boolean;
  isAtLineEnd: boolean;
  isAtDocumentStart: boolean;
  isAtDocumentEnd: boolean;
  
  // Typography context
  fontFamily: string;
  fontSize: number;
  lineHeight: number;
  textDirection: 'ltr' | 'rtl';
}

export interface CaretMovementOptions {
  animate?: boolean;
  animationDuration?: number;
  scrollIntoView?: boolean;
  updateSelection?: boolean;
  preserveHorizontalPosition?: boolean;
  snapToWord?: boolean;
  respectBoundaries?: boolean;
}

export class SmartCaretManager {
  private hitTestingEngine: PrecisionHitTestingEngine;
  private performanceMonitor: CaretPerformanceMonitor;
  private currentState: CaretState;
  private config: CaretSystemConfig;
  private systemState: SystemState;
  
  // Animation and rendering
  private animationFrameId: number | null = null;
  private blinkIntervalId: number | null = null;
  private renderCallback?: (state: CaretState) => void;
  
  // Performance optimization
  private lastMousePosition: { x: number; y: number } | null = null;
  private debounceTimeout: number | null = null;
  private preferredHorizontalPosition: number | null = null;
  
  // Event handling
  private eventListeners: Map<string, Function[]> = new Map();
  
  constructor(
    hitTestingEngine: PrecisionHitTestingEngine,
    config: Partial<CaretSystemConfig> = {}
  ) {
    this.hitTestingEngine = hitTestingEngine;
    this.performanceMonitor = new CaretPerformanceMonitor();
    
    // Initialize configuration with defaults
    this.config = {
      coordinateSpace: CoordinateSpace.CANVAS,
      precision: 'subpixel',
      performance: 'optimized',
      blinkRate: 530, // Standard caret blink rate (ms)
      animationDuration: 150, // Smooth movement duration (ms)
      hitTestTolerance: 3, // Pixel tolerance for hit testing
      enableAnimations: true,
      enableBlinking: true,
      enableKeyboardNavigation: true,
      enableAccessibility: true,
      debugMode: false,
      features: {
        caching: true,
        spatialIndexing: true,
        animations: true,
        legalDocumentSupport: true,
        performanceMonitoring: true
      },
      thresholds: {
        hitTestTimeout: 10,
        cacheSize: 1000,
        spatialIndexDepth: 8
      },
      ...config
    };
    
    // Initialize system state
    this.systemState = {
      isInitialized: false,
      isActive: false,
      hasLayoutPlan: false,
      currentLayoutHash: null,
      lastUpdateTime: performance.now(),
      performanceStatus: 'good',
      lastError: null,
      operationCount: 0,
      errorCount: 0,
      performanceMetrics: {
        hitTestTime: 0,
        positioningTime: 0,
        renderingTime: 0,
        totalOperations: 0,
        cacheHitRate: 0,
        memoryUsage: { spatialIndex: 0, positionCache: 0, total: 0 }
      }
    };
    
    // Initialize caret state
    this.currentState = {
      position: null,
      isVisible: false,
      isBlinking: false,
      lastUpdateTime: performance.now(),
      animationState: 'idle',
      context: this.createDefaultContext()
    };
    
    console.log('[SmartCaretManager] Initialized with intelligent positioning');
  }
  
  // ============================================================================
  // INITIALIZATION AND LIFECYCLE
  // ============================================================================
  
  /**
   * Initialize the caret manager with layout plan
   */
  async initialize(layoutPlan: any, planHash: string): Promise<ValidationResult> {
    const endTiming = this.performanceMonitor.startOperation('initialize');
    
    try {
      // Update hit testing engine with new layout
      this.hitTestingEngine.updateLayoutPlan(layoutPlan, planHash);
      
      // Validate system readiness
      const validation = this.validateSystemReadiness();
      if (!validation.isValid) {
        return validation;
      }
      
      // Update system state
      this.systemState = {
        ...this.systemState,
        isInitialized: true,
        hasLayoutPlan: true,
        performanceStatus: 'excellent'
      };
      
      // Start performance monitoring
      this.startPerformanceMonitoring();
      
      console.log('[SmartCaretManager] Initialization complete');
      
      return {
        isValid: true,
        errors: [],
        warnings: [],
        performance: this.performanceMonitor.getMetrics()
      };
      
    } catch (error) {
      console.error('[SmartCaretManager] Initialization failed:', error);
      
      this.systemState.lastError = {
        type: 'initialization',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: Date.now(),
        context: { layoutPlan: !!layoutPlan, layoutPlanHash: planHash }
      };
      
      return {
        isValid: false,
        errors: [`Initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`],
        warnings: [],
        performance: this.performanceMonitor.getMetrics()
      };
      
    } finally {
      endTiming();
    }
  }
  
  /**
   * Activate caret management (start blinking, event handling)
   */
  activate(): void {
    if (!this.systemState.isInitialized) {
      console.warn('[SmartCaretManager] Cannot activate: not initialized');
      return;
    }
    
    this.systemState.isActive = true;
    
    if (this.config.enableBlinking) {
      this.startBlinking();
    }
    
    console.log('[SmartCaretManager] Activated');
    this.emit('activated', { timestamp: performance.now() });
  }
  
  /**
   * Deactivate caret management
   */
  deactivate(): void {
    this.systemState.isActive = false;
    
    this.stopBlinking();
    this.stopAnimation();
    
    console.log('[SmartCaretManager] Deactivated');
    this.emit('deactivated', { timestamp: performance.now() });
  }
  
  // ============================================================================
  // MOUSE-BASED POSITIONING
  // ============================================================================
  
  /**
   * Position caret based on mouse click with intelligent context awareness
   */
  async positionFromMouse(
    canvasX: number, 
    canvasY: number, 
    options: CaretMovementOptions = {}
  ): Promise<CaretPosition | null> {
    const endTiming = this.performanceMonitor.startOperation('positionFromMouse');
    
    try {
      // Debounce rapid mouse movements
      if (this.shouldDebounceMouseMovement(canvasX, canvasY)) {
        return this.currentState.position;
      }
      
      // Perform precision hit testing
      const hitResult = this.hitTestingEngine.hitTest(canvasX, canvasY);
      
      if (!hitResult || hitResult.confidence < 0.3) {
        console.log('[SmartCaretManager] Low confidence hit test result:', hitResult?.confidence);
        return null;
      }
      
      // Create intelligent caret position with context
      const caretPosition = await this.createIntelligentCaretPosition(
        hitResult.position,
        { mouseClick: { x: canvasX, y: canvasY } }
      );
      
      // Apply smart positioning adjustments
      const adjustedPosition = this.applySmartPositioning(caretPosition, options);
      
      // Update caret state with animation
      await this.updateCaretPosition(adjustedPosition, options);
      
      // Update preferred horizontal position for vertical navigation
      this.preferredHorizontalPosition = adjustedPosition.coordinates.x;
      
      console.log('[SmartCaretManager] Positioned caret at index:', adjustedPosition.index);
      
      return adjustedPosition;
      
    } catch (error) {
      console.error('[SmartCaretManager] Mouse positioning failed:', error);
      return null;
    } finally {
      endTiming();
    }
  }
  
  /**
   * Handle mouse movement for caret preview (without committing position)
   */
  previewCaretPosition(canvasX: number, canvasY: number): CaretPosition | null {
    // Quick hit test for preview (no animation, no state update)
    const hitResult = this.hitTestingEngine.hitTest(canvasX, canvasY);
    
    if (!hitResult || hitResult.confidence < 0.5) {
      return null;
    }
    
    return hitResult.position;
  }
  
  // ============================================================================
  // KEYBOARD NAVIGATION
  // ============================================================================
  
  /**
   * Handle keyboard navigation with intelligent positioning
   */
  async navigateWithKeyboard(navigation: KeyboardNavigation): Promise<CaretPosition | null> {
    if (!this.config.enableKeyboardNavigation || !this.currentState.position) {
      return null;
    }
    
    const endTiming = this.performanceMonitor.startOperation('keyboardNavigation');
    
    try {
      const currentPosition = this.currentState.position;
      let targetIndex = currentPosition.index;
      
      // Calculate target position based on navigation type
      switch (navigation.direction) {
        case 'left':
          targetIndex = Math.max(0, targetIndex - (navigation.unit === 'word' ? this.getWordLength(targetIndex, 'left') : 1));
          break;
          
        case 'right':
          targetIndex = targetIndex + (navigation.unit === 'word' ? this.getWordLength(targetIndex, 'right') : 1);
          break;
          
        case 'up':
          targetIndex = await this.getVerticalNavigationTarget(targetIndex, 'up');
          break;
          
        case 'down':
          targetIndex = await this.getVerticalNavigationTarget(targetIndex, 'down');
          break;
          
        case 'home':
          targetIndex = navigation.unit === 'document' ? 0 : this.getLineStart(targetIndex);
          break;
          
        case 'end':
          targetIndex = navigation.unit === 'document' ? this.getDocumentLength() : this.getLineEnd(targetIndex);
          break;
      }
      
      // Get character position for target index
      const targetCoordinates = this.hitTestingEngine.getCharacterPosition(targetIndex);
      if (!targetCoordinates) {
        return null;
      }
      
      // Create new caret position
      const newPosition: CaretPosition = {
        ...currentPosition,
        index: targetIndex,
        coordinates: targetCoordinates
      };
      
      // Apply context-aware adjustments
      const contextPosition = await this.createIntelligentCaretPosition(newPosition, {
        keyboardNavigation: navigation
      });
      
      // Update caret with animation
      await this.updateCaretPosition(contextPosition, {
        animate: this.config.enableAnimations,
        preserveHorizontalPosition: navigation.direction === 'up' || navigation.direction === 'down'
      });
      
      return contextPosition;
      
    } catch (error) {
      console.error('[SmartCaretManager] Keyboard navigation failed:', error);
      return null;
    } finally {
      endTiming();
    }
  }
  
  // ============================================================================
  // INTELLIGENT POSITIONING LOGIC
  // ============================================================================
  
  /**
   * Create intelligent caret position with full context analysis
   */
  private async createIntelligentCaretPosition(
    basePosition: CaretPosition,
    context: { mouseClick?: { x: number; y: number }; keyboardNavigation?: KeyboardNavigation } = {}
  ): Promise<CaretPosition> {
    
    // Analyze document structure context
    const documentContext = this.analyzeDocumentContext(basePosition.index);
    
    // Analyze typography context
    const typographyContext = this.analyzeTypographyContext(basePosition);
    
    // Create enhanced caret context
    const caretContext: CaretContext = {
      ...this.createDefaultContext(),
      ...documentContext,
      ...typographyContext,
      isAtWordBoundary: this.isAtWordBoundary(basePosition.index),
      isAtLineStart: this.isAtLineStart(basePosition.index),
      isAtLineEnd: this.isAtLineEnd(basePosition.index),
      isAtDocumentStart: basePosition.index === 0,
      isAtDocumentEnd: basePosition.index >= this.getDocumentLength()
    };
    
    // Enhanced caret position with intelligence
    return {
      ...basePosition,
      context: {
        ...basePosition.context,
        ...caretContext
      },
      metadata: {
        ...basePosition.metadata,
        method: 'intelligent',
        confidence: this.calculatePositionConfidence(basePosition, caretContext),
        timing: {
          ...(basePosition.metadata?.timing || { totalTime: 0 }),
          contextAnalysisTime: performance.now() - (basePosition.coordinates.timestamp || 0),
          totalTime: basePosition.metadata?.timing?.totalTime || 0
        },
        debugInfo: {
          ...(basePosition.metadata?.debugInfo || {}),
          contextAnalysis: {
            documentStructure: documentContext,
            typography: typographyContext,
            inputMethod: context.mouseClick ? 'mouse' : 'keyboard'
          }
        }
      }
    };
  }
  
  /**
   * Apply smart positioning adjustments based on context
   */
  private applySmartPositioning(
    position: CaretPosition, 
    options: CaretMovementOptions
  ): CaretPosition {
    
    let adjustedPosition = { ...position };
    
    // Smart word boundary snapping
    if (options.snapToWord && this.shouldSnapToWordBoundary(position)) {
      adjustedPosition = this.snapToNearestWordBoundary(adjustedPosition);
    }
    
    // Legal document boundary respect
    if (options.respectBoundaries) {
      adjustedPosition = this.respectLegalDocumentBoundaries(adjustedPosition);
    }
    
    // Citation and clause awareness
    if (position.context.isInCitation || position.context.isInClause) {
      adjustedPosition = this.applyLegalContextAdjustments(adjustedPosition);
    }
    
    return adjustedPosition;
  }
  
  // ============================================================================
  // ANIMATION AND VISUAL FEEDBACK
  // ============================================================================
  
  /**
   * Update caret position with smooth animation
   */
  private async updateCaretPosition(
    newPosition: CaretPosition,
    options: CaretMovementOptions = {}
  ): Promise<void> {
    
    const previousPosition = this.currentState.position;
    const shouldAnimate = options.animate !== false && this.config.enableAnimations && previousPosition;
    
    // Update state immediately for responsiveness
    this.currentState.position = newPosition;
    this.currentState.lastUpdateTime = performance.now();
    
    if (shouldAnimate && previousPosition) {
      // Start smooth animation
      await this.animateCaretMovement(previousPosition, newPosition, options.animationDuration);
    } else {
      // Immediate update
      this.currentState.animationState = 'idle';
      this.renderCaret();
    }
    
    // Update context
    this.currentState.context = this.analyzeCurrentContext();
    
    // Emit position change event
    this.emit('positionChanged', {
      position: newPosition,
      previousPosition,
      animated: shouldAnimate,
      timestamp: performance.now()
    });
    
    // Handle scroll into view
    if (options.scrollIntoView !== false) {
      this.scrollCaretIntoView();
    }
  }
  
  /**
   * Animate smooth caret movement between positions
   */
  private async animateCaretMovement(
    fromPosition: CaretPosition,
    toPosition: CaretPosition,
    duration: number = this.config.animationDuration
  ): Promise<void> {
    
    return new Promise((resolve) => {
      const startTime = performance.now();
      const startX = fromPosition.coordinates.x;
      const startY = fromPosition.coordinates.y;
      const endX = toPosition.coordinates.x;
      const endY = toPosition.coordinates.y;
      
      this.currentState.animationState = 'moving';
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease-out cubic)
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        
        // Interpolate position
        const currentX = startX + (endX - startX) * easeProgress;
        const currentY = startY + (endY - startY) * easeProgress;
        
        // Update animated position
        this.currentState.position = {
          ...toPosition,
          coordinates: {
            ...toPosition.coordinates,
            x: currentX,
            y: currentY
          }
        };
        
        this.renderCaret();
        
        if (progress < 1) {
          this.animationFrameId = requestAnimationFrame(animate);
        } else {
          this.currentState.animationState = 'idle';
          this.currentState.position = toPosition;
          this.renderCaret();
          resolve();
        }
      };
      
      this.animationFrameId = requestAnimationFrame(animate);
    });
  }
  
  // ============================================================================
  // BLINKING AND VISUAL STATE
  // ============================================================================
  
  /**
   * Start caret blinking animation
   */
  private startBlinking(): void {
    if (this.blinkIntervalId !== null) {
      return;
    }
    
    this.currentState.isVisible = true;
    this.currentState.isBlinking = true;
    
    this.blinkIntervalId = window.setInterval(() => {
      if (this.systemState.isActive && this.currentState.isBlinking) {
        this.currentState.isVisible = !this.currentState.isVisible;
        this.renderCaret();
      }
    }, this.config.blinkRate);
    
    console.log('[SmartCaretManager] Blinking started');
  }
  
  /**
   * Stop caret blinking
   */
  private stopBlinking(): void {
    if (this.blinkIntervalId !== null) {
      clearInterval(this.blinkIntervalId);
      this.blinkIntervalId = null;
    }
    
    this.currentState.isBlinking = false;
    this.currentState.isVisible = true;
    
    console.log('[SmartCaretManager] Blinking stopped');
  }
  
  /**
   * Stop any running animations
   */
  private stopAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    this.currentState.animationState = 'idle';
  }
  
  // ============================================================================
  // CONTEXT ANALYSIS
  // ============================================================================
  
  /**
   * Analyze document structure context at position
   */
  private analyzeDocumentContext(index: number): Partial<CaretContext> {
    // This would integrate with the document structure analysis
    // For now, return basic analysis
    return {
      isInParagraph: true,
      isInCitation: false, // Would check citation ranges
      isInClause: false,   // Would check clause boundaries
      isInHeader: false,   // Would check header regions
      isInFooter: false,   // Would check footer regions
      paragraphType: 'normal'
    };
  }
  
  /**
   * Analyze typography context at position
   */
  private analyzeTypographyContext(position: CaretPosition): Partial<CaretContext> {
    return {
      fontFamily: 'Times New Roman', // Would get from layout plan
      fontSize: 12,                  // Would get from layout plan
      lineHeight: 16.8,              // Would calculate from layout
      textDirection: 'ltr'           // Would determine from text analysis
    };
  }
  
  /**
   * Analyze current caret context
   */
  private analyzeCurrentContext(): CaretContext {
    if (!this.currentState.position) {
      return this.createDefaultContext();
    }
    
    const position = this.currentState.position;
    
    return {
      ...this.createDefaultContext(),
      ...this.analyzeDocumentContext(position.index),
      ...this.analyzeTypographyContext(position),
      isAtWordBoundary: this.isAtWordBoundary(position.index),
      isAtLineStart: this.isAtLineStart(position.index),
      isAtLineEnd: this.isAtLineEnd(position.index),
      isAtDocumentStart: position.index === 0,
      isAtDocumentEnd: position.index >= this.getDocumentLength()
    };
  }
  
  /**
   * Create default caret context
   */
  private createDefaultContext(): CaretContext {
    return {
      isInParagraph: false,
      isInCitation: false,
      isInClause: false,
      isInHeader: false,
      isInFooter: false,
      paragraphType: 'normal',
      isAtWordBoundary: false,
      isAtLineStart: false,
      isAtLineEnd: false,
      isAtDocumentStart: false,
      isAtDocumentEnd: false,
      fontFamily: 'Times New Roman',
      fontSize: 12,
      lineHeight: 16.8,
      textDirection: 'ltr'
    };
  }
  
  // ============================================================================
  // UTILITY METHODS
  // ============================================================================
  
  /**
   * Check if mouse movement should be debounced
   */
  private shouldDebounceMouseMovement(x: number, y: number): boolean {
    if (!this.lastMousePosition) {
      this.lastMousePosition = { x, y };
      return false;
    }
    
    const distance = Math.sqrt(
      Math.pow(x - this.lastMousePosition.x, 2) + 
      Math.pow(y - this.lastMousePosition.y, 2)
    );
    
    this.lastMousePosition = { x, y };
    
    // Debounce if movement is less than 2 pixels
    return distance < 2;
  }
  
  /**
   * Calculate position confidence based on context
   */
  private calculatePositionConfidence(position: CaretPosition, context: CaretContext): number {
    let confidence = position.metadata?.confidence || 0.5;
    
    // Boost confidence for word boundaries
    if (context.isAtWordBoundary) confidence += 0.1;
    
    // Boost confidence for line boundaries
    if (context.isAtLineStart || context.isAtLineEnd) confidence += 0.1;
    
    // Boost confidence for legal structure boundaries
    if (context.isInCitation || context.isInClause) confidence += 0.15;
    
    return Math.min(1.0, confidence);
  }
  
  /**
   * Render caret (calls external render callback)
   */
  private renderCaret(): void {
    if (this.renderCallback) {
      this.renderCallback(this.currentState);
    }
  }
  
  /**
   * Scroll caret into view if needed
   */
  private scrollCaretIntoView(): void {
    // Implementation would handle viewport scrolling
    // For now, just emit event
    this.emit('scrollRequested', {
      position: this.currentState.position,
      timestamp: performance.now()
    });
  }
  
  // ============================================================================
  // PLACEHOLDER METHODS (TO BE IMPLEMENTED)
  // ============================================================================
  
  private validateSystemReadiness(): ValidationResult {
    return {
      isValid: true,
      errors: [],
      warnings: [],
      performance: this.performanceMonitor.getMetrics()
    };
  }
  
  private startPerformanceMonitoring(): void {
    // Start performance monitoring
  }
  
  private getWordLength(index: number, direction: 'left' | 'right'): number {
    return 1; // Placeholder
  }
  
  private async getVerticalNavigationTarget(index: number, direction: 'up' | 'down'): Promise<number> {
    return index; // Placeholder
  }
  
  private getLineStart(index: number): number {
    return index; // Placeholder
  }
  
  private getLineEnd(index: number): number {
    return index; // Placeholder
  }
  
  private getDocumentLength(): number {
    return 1000; // Placeholder
  }
  
  private isAtWordBoundary(index: number): boolean {
    return false; // Placeholder
  }
  
  private isAtLineStart(index: number): boolean {
    return false; // Placeholder
  }
  
  private isAtLineEnd(index: number): boolean {
    return false; // Placeholder
  }
  
  private shouldSnapToWordBoundary(position: CaretPosition): boolean {
    return false; // Placeholder
  }
  
  private snapToNearestWordBoundary(position: CaretPosition): CaretPosition {
    return position; // Placeholder
  }
  
  private respectLegalDocumentBoundaries(position: CaretPosition): CaretPosition {
    return position; // Placeholder
  }
  
  private applyLegalContextAdjustments(position: CaretPosition): CaretPosition {
    return position; // Placeholder
  }
  
  // ============================================================================
  // EVENT SYSTEM
  // ============================================================================
  
  /**
   * Add event listener
   */
  on(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }
  
  /**
   * Remove event listener
   */
  off(event: string, callback: Function): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }
  
  /**
   * Emit event
   */
  private emit(event: string, data: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[SmartCaretManager] Event listener error for '${event}':`, error);
        }
      });
    }
  }
  
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  
  /**
   * Set render callback for caret visualization
   */
  setRenderCallback(callback: (state: CaretState) => void): void {
    this.renderCallback = callback;
  }
  
  /**
   * Get current caret state
   */
  getState(): CaretState {
    return { ...this.currentState };
  }
  
  /**
   * Get current caret position
   */
  getPosition(): CaretPosition | null {
    return this.currentState.position;
  }
  
  /**
   * Get system status
   */
  getSystemStatus(): SystemState & { performance: any } {
    return {
      ...this.systemState,
      performance: this.performanceMonitor.getMetrics()
    };
  }
  
  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<CaretSystemConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Apply configuration changes
    if (newConfig.enableBlinking !== undefined) {
      if (newConfig.enableBlinking && this.systemState.isActive) {
        this.startBlinking();
      } else {
        this.stopBlinking();
      }
    }
    
    console.log('[SmartCaretManager] Configuration updated');
  }
  
  /**
   * Cleanup resources
   */
  destroy(): void {
    this.deactivate();
    this.stopAnimation();
    this.eventListeners.clear();
    
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    
    console.log('[SmartCaretManager] Destroyed');
  }
}
